import { twMerge } from 'tailwind-merge';
import { resolveIouTier, KPI_TIER_COLORS } from '@/utils/kpi-dashboard.utils';

interface MIouGaugeProps {
  /** Mean IoU expressed between 0 and 1. */
  value: number;
  label?: string;
  containerClassName?: string;
}

const VIEWBOX = 240;
const CENTER = VIEWBOX / 2;
const ARC_WIDTH = 22;
// const ARC_RADIUS = 92 - ARC_WIDTH / 2;
const ARC_RADIUS = 81;
const ARC_LENGTH = 2 * Math.PI * ARC_RADIUS;
const POLAR_AXIS_RADIUS = 107;
const POLAR_TICK_RADIUS = 115;
const POLAR_TICK_COUNT = 10;

const polarPoint = (index: number, radius: number) => {
  const angle = (-90 + (index * 360) / POLAR_TICK_COUNT) * (Math.PI / 180);

  return {
    x: CENTER + radius * Math.cos(angle),
    y: CENTER + radius * Math.sin(angle),
  };
};

const polarAxisPoints = Array.from({ length: POLAR_TICK_COUNT }, (_, index) => {
  const { x, y } = polarPoint(index, POLAR_AXIS_RADIUS);

  return `${x},${y}`;
}).join(' ');

const polarTicks = Array.from({ length: POLAR_TICK_COUNT }, (_, index) => ({
  inner: polarPoint(index, POLAR_AXIS_RADIUS),
  outer: polarPoint(index, POLAR_TICK_RADIUS),
}));

export const MIouGauge = ({
  value,
  label = 'mIoU',
  containerClassName,
}: MIouGaugeProps) => {
  const ratio = Math.min(Math.max(value, 0), 1);
  const tierColor = KPI_TIER_COLORS[resolveIouTier(ratio)];

  return (
    <div className={twMerge('relative h-56 w-full', containerClassName)}>
      <svg
        viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
        className="h-full w-full"
        role="img"
        aria-label={`${label} ${(ratio * 100).toFixed(1)}%`}
      >
        <polygon
          points={polarAxisPoints}
          fill="none"
          stroke="#EAECF0"
          strokeWidth={1}
        />
        {polarTicks.map(({ inner, outer }, index) => (
          <line
            key={index}
            x1={inner.x}
            y1={inner.y}
            x2={outer.x}
            y2={outer.y}
            stroke="#EAECF0"
            strokeWidth={1}
          />
        ))}
        <circle
          cx={CENTER}
          cy={CENTER}
          r={ARC_RADIUS}
          fill="none"
          stroke="#F1F1F4"
          strokeWidth={ARC_WIDTH}
        />
        <circle
          cx={CENTER}
          cy={CENTER}
          r={ARC_RADIUS}
          fill="none"
          stroke={tierColor?.solid}
          strokeWidth={ARC_WIDTH}
          strokeLinecap="round"
          strokeDasharray={`${ratio * ARC_LENGTH} ${ARC_LENGTH}`}
          transform={`rotate(-90 ${CENTER} ${CENTER})`}
        />
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xs uppercase tracking-widest text-gray-78829D">
          {label}
        </span>
        <span
          className="text-4xl font-bold tabular-nums"
          style={{ color: tierColor?.text }}
        >
          {(ratio * 100).toFixed(1)}%
        </span>
        <span className="mt-1 text-xs text-gray-78829D">
          {ratio.toFixed(3)}
        </span>
      </div>
    </div>
  );
};
